import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Dialog, FormItem } from 'vnetwork-ui'
import AsyncSelectWithServer from '@/components/Form/Shared/AsyncSelectWithServer'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useAuthStore from '@/stores/useAuthStore'
import { getEmployeesOptions } from '@/modules/Employee/services'
import { assignTicket, bulkAssignTickets } from '@/modules/Ticket/services'
import { Ticket } from '@/modules/Ticket/types/response'
import { ModalProps } from '@/types/shared'

export interface AssignTicketModalProps extends ModalProps {
    tickets: Ticket[]
    onSuccess?: () => void
}

const AssignTicketModal: React.FC<AssignTicketModalProps> = ({
    onClose,
    open,
    tickets,
    onSuccess,
}) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const { employee } = useAuthStore()

    const { control, watch, reset } = useForm({
        defaultValues: {
            technical_assignees: [] as number[],
            reason: '',
        },
    })

    const assigneeIds = watch('technical_assignees')
    const reason = watch('reason')

    // Check if ticket(s) have existing assignees
    const hasExistingAssignees = useMemo(() => {
        if (tickets.length === 1) {
            return (
                Array.isArray(tickets[0]?.ticket_technical_assignees) &&
                tickets[0].ticket_technical_assignees.length > 0
            )
        }
        // For bulk, check if any ticket has assignees
        return tickets.some(
            (t) =>
                Array.isArray(t?.ticket_technical_assignees) &&
                t.ticket_technical_assignees.length > 0
        )
    }, [tickets])

    // Check if user is self-assigning (from empty to only current user)
    const isSelfAssigning = useMemo(() => {
        if (!employee?.employee_id) return false
        if (hasExistingAssignees) return false
        return (
            assigneeIds.length === 1 && assigneeIds[0] === employee.employee_id
        )
    }, [assigneeIds, employee?.employee_id, hasExistingAssignees])

    // Determine if reason is required
    const needsReason = hasExistingAssignees && !isSelfAssigning

    // Single assign mutation
    const { mutateAsync: assignTicketMutation, isPending: isAssigning } =
        useMutationWithHandler(
            {
                mutationFn: assignTicket,
            },
            {
                reactQueryKeys: QUERY_KEYS.TICKETS,
                showSuccessToast: true,
                showErrorToast: true,
            }
        )

    // Bulk assign mutation
    const { mutateAsync: bulkAssignMutation, isPending: isBulkAssigning } =
        useMutationWithHandler(
            {
                mutationFn: bulkAssignTickets,
            },
            {
                reactQueryKeys: QUERY_KEYS.TICKETS,
                showSuccessToast: true,
                showErrorToast: true,
            }
        )

    const handleClose = () => {
        reset({ technical_assignees: [], reason: '' })
        onClose()
    }

    const handleAssign = async () => {
        if (assigneeIds.length === 0 || tickets.length === 0) return

        try {
            if (tickets.length === 1) {
                // Single assign
                await assignTicketMutation({
                    ticket_id: tickets[0]?.ticket_id as number,
                    technical_assignees: assigneeIds,
                    reason: needsReason ? reason : undefined,
                })
            } else {
                // Bulk assign
                await bulkAssignMutation({
                    ticket_ids: tickets.map((t) => t?.ticket_id as number),
                    technical_assignees: assigneeIds,
                    reason: needsReason ? reason : undefined,
                })
            }

            // Refresh the list
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TICKETS] })
            onSuccess?.()
            handleClose()
        } catch (error) {
            console.error('Assign failed:', error)
        }
    }

    // Check if can submit
    const canSubmit =
        assigneeIds.length > 0 &&
        (!needsReason || (needsReason && reason.trim().length > 0))

    const isSingleTicket = tickets.length === 1
    const isLoading = isAssigning || isBulkAssigning

    return (
        <Dialog
            onClose={handleClose}
            open={open}
            title={
                isSingleTicket
                    ? t('assign-ticket')
                    : t('bulk-assign-tickets', { count: tickets.length })
            }
            children={
                <div className="space-y-4 overflow-visible">
                    <div className="text-sm text-gray-600">
                        {isSingleTicket ? (
                            <p>
                                {t('assign-ticket-description', {
                                    ticketId: tickets[0]?.ticket_id,
                                })}
                            </p>
                        ) : (
                            <p>
                                {t('bulk-assign-description', {
                                    count: tickets.length,
                                })}
                            </p>
                        )}
                    </div>

                    <AsyncSelectWithServer
                        control={control}
                        name="technical_assignees"
                        label={t('select-assignees')}
                        placeholder={t('select-assignees')}
                        asyncOptions={getEmployeesOptions}
                        multiple
                    />

                    {/* Self-assign message - khi từ [] chỉ assign cho chính mình */}
                    {isSelfAssigning && (
                        <Alert variant="info" size="small">
                            {t('self-assign-message')}
                        </Alert>
                    )}

                    {/* Reason field - khi đổi assignees (có assignees cũ) */}
                    {needsReason && (
                        <FormItem
                            control={control}
                            name="reason"
                            type="textarea"
                            label={t('assign-change-reason') + ' *'}
                            placeholder={t('assign-change-reason-placeholder')}
                        />
                    )}
                </div>
            }
            actions={
                <div className="flex items-center gap-2">
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAssign}
                        disabled={!canSubmit || isLoading}
                        loading={isLoading}
                    >
                        {t('assign')}
                    </Button>
                </div>
            }
        />
    )
}

export default AssignTicketModal
